import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import intl from 'react-intl-universal'
import {
    CenterPopup, Button, Picker,
    CascadePicker, Stepper, Form,
    Popup, Card, Toast
} from 'antd-mobile'
import { CloseOutline, DeleteOutline } from 'antd-mobile-icons'
import styles from './modal.module.less'
import CurrencySymbol from '../../components/CurrencySymbol'
import { selectProductModalVisible, setProductModalVisible } from '../../store/reducers/hotel'
import { selectProductAttr, selectProductGroupAttr, selectProductSpec, resetCart } from '../../store/reducers/cart'
import { judgeOrderModel, submitOrderModel, getStoreInfoByIdModel, uploadImageModel } from '../../model/store'
import { replaceCDNDomain, groupBy } from '../../utils'
import { getScopeParam, getHeadStoreInfoSession } from '../../utils/session/store'
import { getMemberInfo } from '../../utils/storage/store'
import { culTaxAndServiceFee, getDisplayOriginPrice, getPriceSource, getProductBasePrice, isPackagePriceFree, shouldShowAsFree, shouldShowPackageDiscount, sumAttrArrAmount, sumAttrArrOriginAmount } from '../../utils/price'
import { getLanguageStore } from '../../utils/storage/language'
import math, { formatNumber } from '../../utils/math'
import ProductDesc from '../../components/ProductDesc'
import ProductAttrBox from '../../components/ProductAttrBox'
import { formatJSON, getTimeZoneOffset } from '../../utils'
import { calculateBookingTimes, isInSellingTime, isInOpenTime } from '../../utils/bookRule'
import ExtendFormItem from '../../components/ExtendFormItem'
import Signature from '../../components/Signature'
import NoIcon from '../../components/Icon/NoIcon'


// 常量定义
const PRODUCT_TYPES = {
    DIRECT_PURCHASE: 1,
    BOOKING: 2
}

const PRICE_TYPES = {
    FREE: 4,
    HIDDEN: 5
}

const TIME_SELECTION_TYPES = {
    NONE: 2,
    FREE_CHOICE: 3
}

const INITIAL_STATE = {
    loading: false,
    checkBookingTime: null,
    checkBookingDate: null,
    dateColumns: [],
    timeColumns: [],
    dateVisible: false,
    timeVisible: false,
    storeInfo: {},
    quantity: 1,
    signatureImage: null,
    signatureVisible: false,
    imageErrors: {}
}

export default function ProductModal({
    product,
    onCallback,
    iconData,
    showIntro = false,
    moreType = false,
    actionFlag = 1,
    nextFlag = false,
    handlePlaceOrder,
    showExtendForm = true,
    headStoreInfo = {}
}) {
    const [state, setState] = useState(INITIAL_STATE)
    const dispatch = useDispatch()

    const productModalVisible = useSelector(selectProductModalVisible)
    const extendFormRef = useRef(null)
    const attr = useSelector(selectProductAttr)
    const groupAttr = useSelector(selectProductGroupAttr)
    const spec = useSelector(selectProductSpec)

    // 计算价格相关数据
    const { specItem } = spec
    const { attrArr = [] } = attr
    const { attrArr: groupAttrArr = [] } = groupAttr
    const priceSource = getPriceSource({ ...product, specItem })
    const specAmount = getProductBasePrice(priceSource)
    const specOriginAmount = getDisplayOriginPrice(priceSource)

    const normalAttrAmount = sumAttrArrAmount(attrArr)
    const groupAttrAmount = sumAttrArrAmount(groupAttrArr)
    const attrAmount = math.add(normalAttrAmount, groupAttrAmount)
    const attrOriginAmount = math.add(sumAttrArrOriginAmount(attrArr), sumAttrArrOriginAmount(groupAttrArr))

    const unitAmount = shouldShowAsFree(priceSource) ? 0 : math.add(specAmount, attrAmount)
    const amount = math.mul(unitAmount, state.quantity)
    const originAmount = math.mul(math.add(specOriginAmount, attrOriginAmount), state.quantity)
    const priceObj = culTaxAndServiceFee(amount, state.storeInfo, amount)

    // 获取基础数据
    const { areaId } = getScopeParam()
    const { extendColumn } = product
    const { memberId = '' } = getMemberInfo() || {}
    const extData = formatJSON(extendColumn) || {}

    // 计算预约时间数据
    const { bookingTimes, dateColumns } = useMemo(() => {
        if (product.type !== PRODUCT_TYPES.BOOKING) {
            return { bookingTimes: {}, dateColumns: [] }
        }

        const times = calculateBookingTimes(product)
        const { dates = {} } = times || {}
        const columns = Object.keys(dates).map(date => ({
            label: date,
            value: date,
        }))

        return { bookingTimes: times, dateColumns: columns }
    }, [product])

    // 解析扩展字段
    const { extendColumn1 = '', openRange = '', status = '', opening = '', outOfBusinessMessage = '' } = state.storeInfo || {}
    let { otherItems = {}, electronicSignature } = formatJSON(extendColumn1) || {}
    const eSignature = electronicSignature || extData.electronicSignature || false

    if (extData.otherItems && Object.keys(extData.otherItems).length > 0) {
        otherItems = extData.otherItems
    }

    // 判断是否在营业时间
    let message = ''
    if (product && Object.keys(product).length > 0 && state.storeInfo && Object.keys(state.storeInfo).length > 0) {
        const inSellingTime = isInSellingTime(product.sellingTimeRange, product.sellingTimeRangeJson)
        const openObj = isInOpenTime({
            range: openRange,
            status: status,
            opening: opening,
            outOfBusinessMessage
        })


        if (openObj && !openObj.openState) {
            message = `<div class="text-danger">${openObj.message}</div>`
        } else {
            if (!inSellingTime.state) {
                message = `<div class="text-danger">${inSellingTime.message}</div>`
            }
        }

        console.log(message, '///////message///////')
    }


    // 上传签名图片
    const uploadSignatureImage = useCallback(async () => {
        const fileName = `signatureImage_${Date.now()}.jpg`
        const imageData = state.signatureImage.split(',')[1]
        const res = await uploadImageModel({ fileName, imageData })
        return res.url
    }, [state.signatureImage])

    // 获取其他输入值
    const getOtherInputValues = useCallback((formValues) => {
        const result = {}

        Object.keys(otherItems).forEach(item => {
            const { key, inputType, checkFlag, title } = otherItems[item]
            if (checkFlag === '3') return

            let valueStr = ''
            switch (inputType) {
                case 'text':
                case 'radio':
                    valueStr = formValues[key] || ''
                    break
                case 'checkbox':
                    valueStr = (formValues[key] || []).join()
                    break
                default:
                    break
            }

            if (valueStr) {
                result[key] = {
                    title: intl.get(key).d(title),
                    key,
                    value: valueStr
                }
            }
        })

        return result
    }, [otherItems])

    // 构建订单参数
    const buildOrderParams = useCallback((formValues, extendColumn, nAttrObj) => {
        const { email, phone, remark = '', hotelName, firstName, lastName, roomNo, adultNum, childrenNum } = formValues || {}
        const { attrArr = [], attrKeys = '', subAttrKeys = '' } = nAttrObj || {}

        //是否有分组情况
        const hasGroupKey = attrArr?.some(attr => attr.groupKey)
        return {
            areaId,
            memberId,
            lang: getLanguageStore(),
            products: [{
                amount: actionFlag === 2 ? state.quantity : 1,
                isPackage: false,
                productId: product.productId,
                productName: product.productName,
                productTypeName: product.typeName,
                productExtraName: '',
                productExtraNameKey: specItem?.productExtraNameKey || '',
                productExtraAttrSubKeyValues: attrArr?.map(attr => ({
                    key: attr.productExtraAttrSubKey,
                    name: attr.productExtraAttrSubName,
                    num: attr.quantity || 1,
                    parentKey: hasGroupKey ? attr.productExtraAttrKey : '',
                    groupKey: attr.groupKey,
                })),
                price: product.price,
                packagePrice: product.packagePrice,
                orgPrice: shouldShowPackageDiscount(product) ? product.price : product.orgPrice,
                productAmount: unitAmount,
                productType: product.productType
            }],
            storeId: product.storeId,
            totalPrice: priceObj.pd_actual_price,
            timezoneOffset: getTimeZoneOffset(),
            lat: '',
            lng: '',
            remark,
            firstName,
            lastName,
            roomNo,
            phoneNumber: phone,
            email,
            extendColumn: JSON.stringify({
                ...extendColumn,
                adultNum: adultNum || '',
                childrenNum: childrenNum || '',
                dinersNum: adultNum || childrenNum ? `${adultNum || 0}/${childrenNum || 0}` : ''
            }),
            orderType: product.type === 4 ? 2 : '' //优惠券产品
        }
    }, [areaId, memberId, actionFlag, state.quantity, product, specItem, attrArr, priceObj, unitAmount])

    // 处理预订逻辑
    const handleBooking = useCallback(async () => {
        try {

            const nList = attrArr.concat(groupAttrArr)
            const nObj = {
                attrArr: nList,
                attrKeys: nList.map(item => item.productExtraAttrKey).join(','),
                subAttrKeys: nList.map(item => item.productExtraAttrSubKey).join(',')
            }
            console.log(nObj, '////====nObj====')

            const nProduct = {
                ...product,
                // ...attr,
                ...nObj,
                specItem,
                quantity: state.quantity
            }

            if (moreType && actionFlag === 1) {
                onCallback?.(nProduct)
                return
            }

            if (nextFlag) {
                handlePlaceOrder?.(nProduct)
                return
            }

            // 检查电子签名
            if (eSignature && !state.signatureImage) {
                Toast.show(intl.get('pls_sign').d('请签名'))
                const $signEl = document.querySelector('.modal-user-signature')
                $signEl?.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth'
                })
                return
            }

            // 验证表单
            const values = await extendFormRef.current?.validateFields() || {}

            // 构建扩展字段
            const extendColumn = {
                bookDate: state.checkBookingDate?.value,
                bookTime: state.checkBookingTime?.value,
                electronicSignature: '',
                hotelName: values?.hotelName?.[0] || ''
            }

            // 添加其他填写项
            if (Object.keys(otherItems).length > 0) {
                const otherObj = getOtherInputValues(values)
                if (Object.keys(otherObj).length > 0) {
                    extendColumn.otherObj = otherObj
                }
            }

            setState(prev => ({ ...prev, loading: true }))

            // 构建订单参数
            const params = buildOrderParams(values, extendColumn, nObj)

            // 判断订单
            await judgeOrderModel(params)
            params.totalPrice = priceObj.totalPrice

            // 处理电子签名
            if (eSignature && state.signatureImage) {
                const url = await uploadSignatureImage()
                extendColumn.electronicSignature = url
                params.extendColumn = JSON.stringify(extendColumn)
            }

            // 提交订单
            const submitResult = await submitOrderModel(params)
            onCallback?.(submitResult.orderId)

            dispatch(resetCart())
            dispatch(setProductModalVisible(false))
        } catch (error) {
            console.error('Booking error:', error)
        } finally {
            setState(prev => ({ ...prev, loading: false }))
        }
    }, [
        product, attr, groupAttr, specItem, state.quantity, state.checkBookingDate,
        state.checkBookingTime, state.signatureImage, moreType, actionFlag,
        nextFlag, eSignature, otherItems, priceObj, onCallback,
        handlePlaceOrder, dispatch, uploadSignatureImage, getOtherInputValues,
        buildOrderParams
    ])

    // 判断按钮是否禁用
    const isDisabled = useCallback((type = PRODUCT_TYPES.DIRECT_PURCHASE) => {
        const hasAttr = product.attrs?.length > 0
        const noAttr = !hasAttr

        let groupAttrLen = 0;
        let normalAttrLen = 0;
        let normalArr = [];
        if (product.attrs?.length > 0) {
            const nGroupAttrs = product.attrs.filter(item => item.groupKey)
            if (nGroupAttrs.length > 0) {
                const groupObj = groupBy(nGroupAttrs, 'groupKey') || {}
                groupAttrLen = Object.keys(groupObj).length
            }

            normalArr = product.attrs.filter(item => !item.groupKey)
            normalAttrLen = normalArr.length
        }

        const hasSpec = product.specs?.length > 0
        const noSpec = product.specs?.length === 0

        // if (noAttr && noSpec) {
        //     return false
        // }

        // if (noAttr && hasSpec) {
        //     return !specItem
        // }


        const attrFlag = []
        if (normalArr.length > 0) {
            normalArr?.forEach(attr => {
                const results = attrArr.filter(item => item.productExtraAttrKey === attr.attrKey)
                if (attr.optionalNum > 0) {
                    const resultQuantity = results.reduce((sum, item) => sum + item.quantity, 0)
                    if (resultQuantity === attr.optionalNum) {
                        attrFlag.push(true)
                    } else {
                        attrFlag.push(false)
                    }
                }
            })
            // console.log(attrFlag, '=====///=======attrFlag====')

        }


        // const noCheckGroupAttr = groupAttrLen === 0 || (groupAttrLen > 0 && groupAttrArr.length === groupAttrLen)
        // const noCheckNormalAttr = normalAttrLen === 0 || (normalAttrLen > 0 && attrArr.length === normalAttrLen)
        // const noCheckedAttr = hasAttr && (!noCheckGroupAttr || !noCheckNormalAttr)
        const noCheckGroupAttr = groupAttrLen === 0 || (groupAttrLen > 0 && groupAttrArr.length === groupAttrLen)
        const noCheckNormalAttr = normalAttrLen === 0 || (normalAttrLen > 0 && attrFlag.every(item => item === true))
        const noCheckedAttr = hasAttr && (!noCheckGroupAttr || !noCheckNormalAttr)


        if (type === PRODUCT_TYPES.BOOKING) {
            // const hasAttr = product.attrs?.length > 0
            // const noAttr = !hasAttr
            const noCheckedDate = nextFlag ? false : (
                !state.checkBookingDate ||
                (extData.selTimeType !== TIME_SELECTION_TYPES.NONE && !state.checkBookingTime)
            )
            // const noCheckedAttr = hasAttr && !attrArr.length && !groupAttrArr.length

            return (noAttr && noCheckedDate) || (hasAttr && (noCheckedDate || noCheckedAttr))
        } else {
            // return product.attrs?.length > 0 && !attrArr.length && !groupAttrArr.length
            return hasAttr && noCheckedAttr
        }
    }, [product.attrs, attrArr, groupAttrArr, state.checkBookingDate, state.checkBookingTime, nextFlag, extData.selTimeType])

    // 渲染价格显示
    const renderPrice = useCallback(() => {
        if (product.priceType === PRICE_TYPES.HIDDEN) return null

        // 权益套餐免费只展示 0；普通免费价类型展示「免费」
        if (isPackagePriceFree(priceSource) && amount === 0) {
            return (
                <b className='jm-price fs-18'>
                    <CurrencySymbol currency={product.currency} />
                    {formatNumber(0)}
                </b>
            )
        }
        if (product.priceType === PRICE_TYPES.FREE || (shouldShowAsFree(priceSource) && amount === 0)) {
            return (
                <b className='jm-price'>
                    {intl.get('free').d('免费')}
                </b>
            )
        }

        return (
            <b className='jm-price fs-18'>
                <CurrencySymbol currency={product.currency} />
                {formatNumber(amount)}
                {shouldShowPackageDiscount(priceSource) && originAmount > amount && (
                    <small style={{ display: 'block', color: '#777', textDecoration: 'line-through', fontWeight: 400 }}>
                        <CurrencySymbol currency={product.currency} />{formatNumber(originAmount)}
                    </small>
                )}
            </b>
        )
    }, [product.priceType, product.currency, amount, originAmount, priceSource])

    // 渲染税费和服务费
    const renderTaxAndService = useCallback(() => {
        if (Object.keys(priceObj).length === 0 || priceObj.totalPrice <= 0) return null

        return (
            <div className='fs-12'>
                <small className={priceObj.pd_tax_cls}>Tax Fee: {formatNumber(priceObj.pd_tax)}&nbsp;</small>
                <small className={priceObj.pd_service_cls}>Service Fee: {formatNumber(priceObj.pd_service)}&nbsp;</small>
            </div>
        )
    }, [priceObj])

    // 渲染底部按钮栏
    const renderBottomBar = useCallback(() => {
        const headStoreInfoSession = getHeadStoreInfoSession()
        const onlyDisplay = headStoreInfoSession?.isForDisplayOnly || false
        if((headStoreInfo && headStoreInfo.isForDisplayOnly) || onlyDisplay) return null
        if (moreType) {
            return (
                <>
                    <div className='jm-flex-item'>
                        {renderPrice()}
                    </div>
                    <div>
                        <Button
                            size='small'
                            loading={state.loading}
                            disabled={isDisabled(product.type)}
                            className={`jm-btn ${styles.footerBtn}`}
                            onClick={handleBooking}
                        >
                            {actionFlag === 1
                                ? intl.get('addToCart').d('加入购物车')
                                : intl.get('confirmOrdering').d('Place Order')
                            }
                        </Button>
                    </div>
                </>
            )
        }

        if (product.type === PRODUCT_TYPES.BOOKING && product.attrs?.length === 0) {
            return (
                <>
                    <div style={{ width: 30 }}>&nbsp;</div>
                    <div className='jm-flex-item'>
                        <Button
                            block
                            size='small'
                            loading={state.loading}
                            disabled={isDisabled(product.type)}
                            className={`jm-btn ${styles.footerBtn}`}
                            onClick={handleBooking}
                        >
                            {product.type === PRODUCT_TYPES.BOOKING ? intl.get('h_request').d('Request') : intl.get('h_book').d('BOOK')}
                        </Button>
                    </div>
                    <div style={{ width: 30 }}>&nbsp;</div>
                </>
            )
        }

        return (
            <>
                <div className='jm-flex-item'>
                    {renderPrice()}
                    {renderTaxAndService()}
                </div>
                <div>
                    <Button
                        size='small'
                        loading={state.loading}
                        disabled={isDisabled(product.type)}
                        className={`jm-btn ${styles.footerBtn}`}
                        onClick={handleBooking}
                    >
                        {product.type === PRODUCT_TYPES.BOOKING ? intl.get('h_request').d('Request') : intl.get('h_book').d('BOOK')}
                    </Button>
                </div>
            </>
        )
    }, [moreType, product.type, product.attrs, state.loading, actionFlag, isDisabled, renderPrice, renderTaxAndService, handleBooking])

    // 渲染预约时间选择
    const renderBookingItem = useCallback(() => {
        if (nextFlag || product.type !== PRODUCT_TYPES.BOOKING) return null

        return (
            <>
                <div className={`${styles.modalTitle} m-t-sm`}>{intl.get('time_option').d('时间选项')}</div>
                <div className='input-form'>
                    <div className='jm-flex field-item'>
                        <label>{intl.get('h_date').d('DATE')}:</label>
                        <div
                            className='field-content jm-flex-item'
                            onClick={() => setState(prev => ({ ...prev, dateVisible: true }))}
                        >
                            <div className='input-elm'>
                                {state.checkBookingDate?.label ||
                                    <span style={{ color: '#999' }}>{intl.get('pls_select').d('请选择')}</span>
                                }
                            </div>
                        </div>
                    </div>

                    {extData.selTimeType !== TIME_SELECTION_TYPES.NONE && (
                        <div className='jm-flex field-item'>
                            <label>{intl.get('h_time').d('TIME')}:</label>
                            <div
                                className='field-content jm-flex-item'
                                onClick={() => setState(prev => ({ ...prev, timeVisible: true }))}
                            >
                                <div className='input-elm'>
                                    {state.checkBookingTime?.label ||
                                        <span style={{ color: '#999' }}>{intl.get('pls_select').d('请选择')}</span>
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        )
    }, [nextFlag, product.type, extData.selTimeType, state.checkBookingDate, state.checkBookingTime])

    // 渲染电子签名
    const renderSignature = useCallback(() => {
        if (!eSignature) return null

        return (
            <Card className='modal-card' style={{ padding: 0 }}>
                <div style={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    marginBottom: 10,
                    color: 'var(--adm-color-primary)'
                }}>
                    {intl.get('ele_signature').d('Electronic signature')}
                    <span className='text-danger'>*</span>
                </div>
                <div
                    className='modal-user-signature'
                    onClick={() => setState(prev => ({ ...prev, signatureVisible: true }))}
                >
                    {state.signatureImage ? (
                        <div className='pos-rel'>
                            <img
                                src={state.signatureImage}
                                alt="signature"
                                style={{ width: '100%', height: '100%' }}
                            />
                            <div
                                className='del-sign text-center'
                                onClick={e => {
                                    e.stopPropagation()
                                    setState(prev => ({ ...prev, signatureImage: null }))
                                }}
                            >
                                <DeleteOutline fontSize={24} />
                            </div>
                        </div>
                    ) : (
                        <Button>{intl.get('click_to_sign').d('点击签名')}</Button>
                    )}
                </div>
            </Card>
        )
    }, [eSignature, state.signatureImage])

    // 渲染数量选择器
    const renderQuantitySelector = useCallback(() => {
        if (!moreType) return null

        return (
            <div className='input-form'>
                <div className='jm-flex field-item'>
                    <label className='jm-flex-item'>
                        {intl.get('t_amount').d('Amount')}:
                    </label>
                    <div className='field-content text-right'>
                        <Stepper
                            min={1}
                            max={product.orderMaxNum || (product.stock === -1 ? 99 : product.stock)}
                            value={state.quantity}
                            onChange={(value) => setState(prev => ({ ...prev, quantity: value }))}
                        />
                    </div>
                </div>
            </div>
        )
    }, [moreType, state.quantity])

    // 处理日期选择确认
    const handleDateConfirm = useCallback((value, obj) => {
        const timeArr = bookingTimes.dates[value[0]]
        let timeColumns = timeArr?.map(item => ({
            label: `${item.label} ${item.ampm}`,
            value: item.value
        }))

        if (extData.selTimeType === TIME_SELECTION_TYPES.FREE_CHOICE) {
            timeColumns = timeArr
        }

        setState(prev => ({
            ...prev,
            checkBookingDate: obj.items[0],
            checkBookingTime: null,
            timeColumns
        }))
    }, [bookingTimes, extData.selTimeType])

    // 处理时间选择确认
    const handleTimeConfirm = useCallback((value, obj) => {
        if (extData.selTimeType === TIME_SELECTION_TYPES.FREE_CHOICE) {
            const timeStr = value.join(':')
            setState(prev => ({
                ...prev,
                checkBookingTime: { label: timeStr, value: timeStr }
            }))
        } else {
            setState(prev => ({
                ...prev,
                checkBookingTime: obj.items[0]
            }))
        }
    }, [extData.selTimeType])

    // 处理签名保存
    const handleSignatureSave = useCallback((signatureImage) => {
        setState(prev => ({
            ...prev,
            signatureImage,
            signatureVisible: false
        }))
    }, [])

    // 关闭弹窗
    const handleCloseModal = useCallback(() => {
        dispatch(resetCart())
        dispatch(setProductModalVisible(false))
    }, [dispatch])

    // 初始化子属性列表
    useEffect(() => {
        if (product?.attrs?.length > 0) {
            const attrObj = product.attrs[0]
            if (attrObj.groupName) {
                setState(prev => ({
                    ...prev,
                    subAttrList: attrObj.subAttrList
                }))
            }
        }
    }, [product])

    // 获取店铺信息
    useEffect(() => {
        if (product.storeId) {
            getStoreInfoByIdModel({ storeId: product.storeId }).then(res => {
                setState(prev => ({ ...prev, storeInfo: res }))
            }).catch(err => {
                console.log(err)
            })
        }
    }, [product.storeId])

    return (
        <CenterPopup
            visible={productModalVisible}
            onClose={handleCloseModal}
            closeOnMaskClick
            destroyOnClose
            bodyStyle={{ backgroundColor: 'transparent' }}
            style={{ '--min-width': '90%' }}
        >
            <div className={`${styles.productModal} p10`}>
                {iconData && (
                    <div className={styles.modalIconBox}>
                        <div className={styles.iconWrapper}>
                            {state.imageErrors[`icon-${iconData.id}`] ? (
                                <NoIcon width={34} height={34} color='var(--adm-color-primary)' />
                            ) : (
                                <img src={replaceCDNDomain(iconData.imageUrl)} alt={iconData.name}
                                    onError={() => {
                                        setState(prev => ({ ...prev, imageErrors: { ...prev.imageErrors, [`icon-${iconData.id}`]: true } }))
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}

                <div className={`${styles.modalContent} jm-modal-content`}>
                    <div className={styles.modalMain}>
                        <div className={`${styles.modalTitle} ${iconData ? 'text-center' : ''}`}>
                            {product.productName}
                        </div>

                        {(iconData || showIntro) && (
                            <div className={styles.modalDesc}>
                                <ProductDesc list={product.bookingInstructions} />
                            </div>
                        )}

                        {(product.attrs?.length > 0 || product.specs?.length > 0 || product.type === PRODUCT_TYPES.BOOKING) && (
                            <div className={`${styles.attrList} ${styles.contentBox}`}>
                                <ProductAttrBox product={product} />
                                {renderBookingItem()}
                            </div>
                        )}

                        {showExtendForm && !nextFlag && product.type !== 4 && (
                            <Form ref={extendFormRef} className='modalForm m-t-sm'>
                                <ExtendFormItem
                                    storeInfo={state.storeInfo}
                                    productExtendData={extData}
                                    from='modal'
                                />
                            </Form>
                        )}

                        {!nextFlag && renderSignature()}

                        <br />
                        {renderQuantitySelector()}
                    </div>
                </div>

                <div className={`${styles.modalFooter} jm-flex`}>
                    {message ? <div dangerouslySetInnerHTML={{ __html: message }} /> : renderBottomBar()}
                </div>
            </div>

            <div className={styles.modalClose}>
                <CloseOutline
                    fontSize={28}
                    color='#fff'
                    onClick={() => dispatch(setProductModalVisible(false))}
                />
            </div>

            {/* 日期选择器 */}
            <Picker
                confirmText={intl.get('ok').d('确定')}
                cancelText={intl.get('cancel').d('取消')}
                title={intl.get('select_date').d('选择日期')}
                visible={state.dateVisible}
                onClose={() => setState(prev => ({ ...prev, dateVisible: false }))}
                columns={[dateColumns]}
                onConfirm={handleDateConfirm}
            />

            {/* 时间选择器 */}
            {state.timeVisible && (
                extData.selTimeType === TIME_SELECTION_TYPES.FREE_CHOICE ? (
                    <CascadePicker
                        confirmText={intl.get('ok').d('确定')}
                        cancelText={intl.get('cancel').d('取消')}
                        title={intl.get('select_time').d('选择时间')}
                        visible={state.timeVisible}
                        onClose={() => setState(prev => ({ ...prev, timeVisible: false }))}
                        options={state.timeColumns}
                        onConfirm={handleTimeConfirm}
                    />
                ) : (
                    <Picker
                        confirmText={intl.get('ok').d('确定')}
                        cancelText={intl.get('cancel').d('取消')}
                        title={intl.get('select_time').d('选择时间')}
                        visible={state.timeVisible}
                        onClose={() => setState(prev => ({ ...prev, timeVisible: false }))}
                        columns={[state.timeColumns]}
                        onConfirm={handleTimeConfirm}
                    />
                )
            )}

            {/* 签名弹窗 */}
            <Popup
                visible={state.signatureVisible}
                onClose={() => setState(prev => ({ ...prev, signatureVisible: false }))}
                destroyOnClose
                showCloseButton
            >
                <Signature
                    width={window.innerWidth}
                    height={150}
                    onSave={handleSignatureSave}
                />
            </Popup>
        </CenterPopup>
    )
}